import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { generateEAN13 } from "./Products";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Category {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  category_id: string;
  quantity: string;
  department_id: string;
  unit: string;
  supplier_id: string;
}

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [formData, setFormData] = useState<FormData>({
    code: "",
    name: "",
    description: "",
    category_id: "",
    quantity: "",
    department_id: "",
    unit: "UN", // Valor padrão como unidade
    supplier_id: "",
  });

  const UNIT_OPTIONS = [
    { value: "UN", label: "Unidade" },
    { value: "KG", label: "Quilograma" },
    { value: "M", label: "Metro" },
    { value: "M2", label: "Metro Quadrado" },
    { value: "M3", label: "Metro Cúbico" },
    { value: "L", label: "Litro" },
    { value: "CX", label: "Caixa" },
    { value: "PCT", label: "Pacote" },
    { value: "ROL", label: "Rolo" },
  ];

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
    fetchSuppliers();
    if (id) {
      fetchProduct();
    } else {
      setFormData((prev) => ({
        ...prev,
        code: generateEAN13(),
      }));
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as categorias",
        variant: "destructive",
      });
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as secretarias",
        variant: "destructive",
      });
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os fornecedores",
        variant: "destructive",
      });
    }
  };

  const fetchProduct = async () => {
    try {
      console.log("Buscando produto com ID:", id);
      
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          category:categories(id, name),
          inventory:inventory(
            quantity,
            initial_quantity,
            department_id,
            department:departments(id, name)
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;

      if (data) {
        console.log("Dados do produto:", data);
        
        const inventory = data.inventory?.[0];
        console.log("Dados do inventário:", inventory);

        setFormData({
          code: data.code || "",
          name: data.name || "",
          description: data.description || "",
          category_id: data.category_id?.toString() || "",
          quantity: inventory?.quantity?.toString() || "",
          department_id: inventory?.department_id?.toString() || "",
          unit: data.unit || "UN",
          supplier_id: data.supplier_id?.toString() || "",
        });
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o produto",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite o nome do produto",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      let productId = id;
      
      if (!id) {
        // Create new product
        const { data, error } = await supabase.from("products").insert([
          {
            code: formData.code,
            name: formData.name,
            description: formData.description,
            category_id: parseInt(formData.category_id),
            unit: formData.unit,
            supplier_id: formData.supplier_id && formData.supplier_id !== "null" ? parseInt(formData.supplier_id) : null,
          },
        ]).select();

        if (error) throw error;
        
        if (data && data.length > 0) {
          productId = data[0].id;
        }
      } else {
        // Update existing product
        const { error: updateError } = await supabase
          .from("products")
          .update({
            code: formData.code,
            name: formData.name,
            description: formData.description,
            category_id: parseInt(formData.category_id),
            unit: formData.unit,
            supplier_id: formData.supplier_id && formData.supplier_id !== "null" ? parseInt(formData.supplier_id) : null,
          })
          .eq("id", id);

        if (updateError) throw updateError;
      }

      const quantity = parseInt(formData.quantity);
      
      if (id) {
        console.log("Atualizando produto existente com ID:", id);
        console.log("Dados do formulário:", formData);
        
        // Para produtos existentes, verificar se já existe um registro de inventário
        const { data: existingInventories, error: fetchError } = await supabase
          .from("inventory")
          .select("*")
          .eq("product_id", productId);

        if (fetchError) {
          console.error("Erro ao buscar inventário:", fetchError);
          throw fetchError;
        }

        console.log("Registros de inventário encontrados:", existingInventories);

        if (existingInventories && existingInventories.length > 0) {
          // Verificar se já existe um registro para o departamento selecionado
          const existingInventoryForDept = existingInventories.find(
            inv => inv.department_id === parseInt(formData.department_id)
          );
          
          console.log("Registro para o departamento selecionado:", existingInventoryForDept);
          
          if (existingInventoryForDept) {
            // Se já existe um registro para este departamento, apenas atualize a quantidade se foi alterada
            if (formData.quantity !== existingInventoryForDept.quantity.toString()) {
              console.log("Atualizando quantidade no mesmo departamento");
              const { error: updateInventoryError } = await supabase
                .from("inventory")
                .update({
                  quantity: quantity
                })
                .eq("id", existingInventoryForDept.id);
                
              if (updateInventoryError) {
                console.error("Erro ao atualizar inventário:", updateInventoryError);
                throw updateInventoryError;
              }
            } else {
              console.log("Quantidade não foi alterada, nenhuma atualização necessária");
            }
          } else {
            // Se estamos mudando para um novo departamento
            console.log("Mudando produto para um novo departamento");
            
            // 1. Primeiro, excluir todos os registros de inventário existentes para este produto
            console.log("Removendo registros de inventário antigos");
            const { error: deleteError } = await supabase
              .from("inventory")
              .delete()
              .eq("product_id", productId);
              
            if (deleteError) {
              console.error("Erro ao remover registros de inventário antigos:", deleteError);
              throw deleteError;
            }
            
            // 2. Criar um novo registro de inventário para o departamento selecionado
            console.log("Criando novo registro de inventário para o departamento selecionado");
            const { error: insertInventoryError } = await supabase
              .from("inventory")
              .insert([
                {
                  product_id: productId,
                  quantity: quantity,
                  initial_quantity: quantity,
                  department_id: parseInt(formData.department_id),
                },
              ]);

            if (insertInventoryError) {
              console.error("Erro ao criar novo registro de inventário:", insertInventoryError);
              throw insertInventoryError;
            }
          }
        } else {
          // Criar novo inventário para produto existente
          const { error: insertInventoryError } = await supabase
            .from("inventory")
            .insert([
              {
                product_id: productId,
                quantity,
                initial_quantity: quantity,
                department_id: parseInt(formData.department_id),
              },
            ]);

          if (insertInventoryError) throw insertInventoryError;
        }
      } else {
        // Criar inventário para novo produto
        const { error: inventoryError } = await supabase.from("inventory").insert([
          {
            product_id: productId,
            quantity,
            initial_quantity: quantity,
            department_id: parseInt(formData.department_id),
          },
        ]);

        if (inventoryError) throw inventoryError;
      }

      toast({
        title: "Sucesso",
        description: id
          ? "Produto atualizado com sucesso"
          : "Produto criado com sucesso",
      });

      navigate("/products");
    } catch (error) {
      console.error("Error saving product:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o produto",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <div className="container py-6">
      <h1 className="text-2xl font-bold mb-6">
        {id ? "Editar Produto" : "Novo Produto"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="code" className="text-sm font-medium">
              Código *
            </label>
            <Input
              id="code"
              value={formData.code}
              onChange={handleInputChange}
              required
              placeholder="Digite o código do produto"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium">
              Nome *
            </label>
            <Input
              id="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Digite o nome do produto"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="category" className="text-sm font-medium">
              Categoria *
            </label>
            <Select
              value={formData.category_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, category_id: value }))
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="department" className="text-sm font-medium">
              Secretaria *
            </label>
            <Select
              value={formData.department_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, department_id: value }))
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma secretaria" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((department) => (
                  <SelectItem key={department.id} value={department.id.toString()}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="quantity" className="text-sm font-medium">
              Quantidade *
            </label>
            <Input
              id="quantity"
              type="number"
              min="0"
              value={formData.quantity}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, quantity: e.target.value }))
              }
              required
              placeholder="Digite a quantidade"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="unit" className="text-sm font-medium">
              Unidade de Medida *
            </label>
            <Select
              value={formData.unit}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, unit: value }))
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a unidade de medida" />
              </SelectTrigger>
              <SelectContent>
                {UNIT_OPTIONS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="supplier" className="text-sm font-medium">
              Fornecedor
            </label>
            <Select
              value={formData.supplier_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, supplier_id: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um fornecedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-supplier">Nenhum</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id.toString()}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-2">
            <label htmlFor="description" className="text-sm font-medium">
              Descrição
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              className="w-full min-h-[100px] px-3 py-2 border rounded-md"
              placeholder="Digite uma descrição para o produto"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/products")}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Salvando..." : id ? "Salvar Alterações" : "Criar Produto"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
